import React, { useEffect, useMemo, useState } from 'react'

import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'

import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'

import styles from './../Dialog.module.sass'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useSession from 'hooks/useSession'
import HFDropdown from 'components/HFInputs/HFDropdown'

import { managementOpinionSchema } from './ManagementOpinionSchema'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { useFetchList } from 'store'
import { useReload } from 'store'

const ManagementOpinionDialog = (props) => {
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const { className, data, dismiss, resolve } = props
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const [defaultValues, setDefaultValues] = useState(null)
	const reload = useReload()

	const priorities = useMemo(
		() => [
			{
				value: 1,
				text: i18n`Very High`,
				icon: 'very-high',
				className: styles['very-high'],
			},
			{
				value: 2,
				text: i18n`High`,
				icon: 'high',
				className: styles['high'],
				$isSelected: true,
			},
			{
				value: 3,
				text: i18n`Medium`,
				icon: 'medium',
				className: styles['medium'],
			},
			{
				value: 4,
				text: i18n`Low`,
				icon: 'low',
				className: styles['low'],
			},
			{
				value: 5,
				text: i18n`Very Low`,
				icon: 'very-low',
				className: styles['very-low'],
			},
		],
		[i18n]
	)

	const { contents } = useFetchList(
		['metadata/task_eligible_users'],
		[
			(json) => {
				return {
					value: `${json.id}`,
					text: i18n.translate(json.name),
					roles: json.roles,
				}
			},
		]
	)

	const [fetchedUsers] = contents ?? []
	const users = fetchedUsers?.filter(
		(user) => !user?.roles?.includes('doctor')
	)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(managementOpinionSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, reset, formState } = methods

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const endpoint = `${API_URL}/api/clinical_processes/${data?.processId}/administrative_opinion`

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'POST',
				body: formData,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				reload([
					`/clinical_processes/${data?.processId}/tasks`,
					`/medical_opinions/management_opinions`,
				])
				pushNotification(
					undefined,
					'success',
					i18n`Opinion created successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		if (defaultValues && !formState.isDirty) {
			reset && reset(defaultValues)
		}
	}, [reset, defaultValues, formState.isDirty])

	useEffect(() => {
		setDefaultValues({
			priority: priorities[2].value,
		})
	}, [priorities])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_administrative_opinion'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['root'] || ''} ${className || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<small className={styles['dialog-title']}>
							<strong>{i18n`Enter administrative opinion request`}</strong>
						</small>
						<HFTextInput
							placeholder={i18n`Request description`}
							name='description'
							multiline
							noResize
							rows='3'
							required
						/>
						<HFDropdown
							placeholder={i18n`Priority`}
							name='priority'
							options={priorities ?? []}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
						/>
						<HFDropdown
							placeholder={i18n`Target user`}
							name='targetUser'
							options={users ?? []}
							searchable={true}
							optionsAlign='right'
							noData={i18n`No results`}
						/>
					</fieldset>
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_administrative_opinion`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Send`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default ManagementOpinionDialog
